"use client";
import {
  Ahr999Adjusted,
  FearAndGreed,
  GlobalLongShortRatio,
  MVRV,
  NUPL,
  PercentOfSupplyInProfit,
  PuellMultiple,
  PiCycleTop,
  MovingAverages,
  RealizedCap,
  SOPR,
  RelativeUnrealizedProfit,
  ActiveAddresses,
  AddressCountChange,
  MACD,
} from "@/lib/metrics";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Box, IconButton, Link, Popover, Typography } from "@mui/material";
import { useTranslations } from "next-intl";
import { useState } from "react";
import ProTag from "./ProTag";

// 技术面指标
export const TechnicalMenuItems = [
  {
    label: `Metrics.${Ahr999Adjusted.i18nKey}.title`,
    url: `/products/analytics/chart/${Ahr999Adjusted.url}`,
    isPro: Ahr999Adjusted.isPro,
  },
  {
    label: `Metrics.${PiCycleTop.i18nKey}.title`,
    url: `/products/analytics/chart/${PiCycleTop.url}`,
    isPro: PiCycleTop.isPro,
  },
  {
    label: `Metrics.${MovingAverages.i18nKey}.title`,
    url: `/products/analytics/chart/${MovingAverages.url}`,
    isPro: MovingAverages.isPro,
  },
  { label: `Metrics.${MACD.i18nKey}.title`, url: `/products/analytics/chart/${MACD.url}`, isPro: MACD.isPro },
];

// 基本面指标
export const FundamentalMenuItems = [
  { label: `Metrics.${NUPL.i18nKey}.title`, url: `/products/analytics/chart/${NUPL.url}`, isPro: NUPL.isPro },
  { label: `Metrics.${MVRV.i18nKey}.title`, url: `/products/analytics/chart/${MVRV.url}`, isPro: MVRV.isPro },
  {
    label: `Metrics.${PuellMultiple.i18nKey}.title`,
    url: `/products/analytics/chart/${PuellMultiple.url}`,
    isPro: PuellMultiple.isPro,
  },
  // 数据源无法爬取
  // {
  //   label: `Metrics.${PercentOfSupplyInProfit.i18nKey}.title`,
  //   url: `/products/analytics/chart/${PercentOfSupplyInProfit.url}`,
  //   isPro: PercentOfSupplyInProfit.isPro,
  // },
  // {
  //   label: `Metrics.${GlobalLongShortRatio.i18nKey}.title`,
  //   url: `/products/analytics/chart/${GlobalLongShortRatio.url}`,
  //   isPro: GlobalLongShortRatio.isPro,
  // },
  {
    label: `Metrics.${RealizedCap.i18nKey}.title`,
    url: `/products/analytics/chart/${RealizedCap.url}`,
    isPro: RealizedCap.isPro,
  },
  { label: `Metrics.${SOPR.i18nKey}.title`, url: `/products/analytics/chart/${SOPR.url}`, isPro: SOPR.isPro },
  {
    label: `Metrics.${RelativeUnrealizedProfit.i18nKey}.title`,
    url: `/products/analytics/chart/${RelativeUnrealizedProfit.url}`,
    isPro: RelativeUnrealizedProfit.isPro,
  },
];

// 情绪面指标
export const SentimentMenuItems = [
  {
    label: `Metrics.${FearAndGreed.i18nKey}.title`,
    url: `/products/analytics/chart/${FearAndGreed.url}`,
    isPro: FearAndGreed.isPro,
  },
  {
    label: `Metrics.${ActiveAddresses.i18nKey}.title`,
    url: `/products/analytics/chart/${ActiveAddresses.url}`,
    isPro: ActiveAddresses.isPro,
  },
  {
    label: `Metrics.${AddressCountChange.i18nKey}.title`,
    url: `/products/analytics/chart/${AddressCountChange.url}`,
    isPro: AddressCountChange.isPro,
  },
];

export default function BtcPanelSideMenu() {
  const t = useTranslations();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [popoverType, setPopoverType] = useState<"technical" | "fundamental" | "sentimental" | null>(null);

  const handleInfoClick =
    (type: "technical" | "fundamental" | "sentimental") => (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
      setPopoverType(type);
    };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setPopoverType(null);
  };

  const open = Boolean(anchorEl);

  return (
    <Box
      sx={{
        width: "15.5rem",
        py: 4,
        borderRight: (theme) => `1px solid ${theme.palette.divider}`,
        display: { xs: "none", md: "block" },
      }}>
      <Link href="/products/analytics" sx={{ color: "text.primary", textDecoration: "none" }}>
        <Typography
          sx={{
            py: 1.5,
            pl: 6,
            pr: 3,
            fontSize: "var(--font-size-s)",
            fontWeight: 600,
            "&:hover": { bgcolor: "background.paperMedium" },
          }}>
          {t("Analytics.sideMenu.investingSignal")}
        </Typography>
      </Link>

      {/* 技术面指标 */}
      <Box sx={{ display: "flex", alignItems: "center", py: 1, px: 3, mt: 2 }}>
        <Typography
          sx={{
            fontSize: "var(--font-size-xs)",
            fontWeight: 600,
            color: "text.secondary",
            flex: "none",
          }}>
          {t("Analytics.sideMenu.technical")}
        </Typography>
        <IconButton size="small" sx={{ color: "text.secondary" }} onClick={handleInfoClick("technical")}>
          <InfoOutlinedIcon sx={{ fontSize: "1.125rem" }} />
        </IconButton>
      </Box>
      {TechnicalMenuItems.map((item, index) => (
        <Link
          key={index}
          href={item.url}
          sx={{
            minWidth: "100%",
            py: 1.5,
            pl: 6,
            pr: 3,
            color: "text.primary",
            fontWeight: "600",
            textDecoration: "none",
            "&:hover": { bgcolor: "background.paperMedium" },
            display: "block",
            lineHeight: 1.6,
          }}>
          {item.isPro && <ProTag sx={{ display: "inline", mr: 1, position: "relative", top: "-2px" }} />}
          {t(item.label as any)}
        </Link>
      ))}

      {/* 基本面指标 */}
      <Box sx={{ display: "flex", alignItems: "center", py: 1, px: 3, mt: 2 }}>
        <Typography
          sx={{
            fontSize: "var(--font-size-xs)",
            fontWeight: 600,
            color: "text.secondary",
            flex: "none",
          }}>
          {t("Analytics.sideMenu.fundamental")}
        </Typography>
        <IconButton size="small" sx={{ color: "text.secondary" }} onClick={handleInfoClick("fundamental")}>
          <InfoOutlinedIcon sx={{ fontSize: "1.125rem" }} />
        </IconButton>
      </Box>
      {FundamentalMenuItems.map((item, index) => (
        <Link
          key={index}
          href={item.url}
          sx={{
            color: "text.primary",
            fontWeight: "600",
            textDecoration: "none",
            "&:hover": { bgcolor: "background.paperMedium" },
            display: "block",
            py: 1.5,
            pl: 6,
            pr: 3,
          }}>
          {item.isPro && <ProTag sx={{ display: "inline", mr: 1, position: "relative", top: "-2px" }} />}
          {t(item.label as any)}
        </Link>
      ))}

      {/* 情绪面指标 */}
      <Box sx={{ display: "flex", alignItems: "center", py: 1, px: 3, mt: 2 }}>
        <Typography
          sx={{
            fontSize: "var(--font-size-xs)",
            fontWeight: 600,
            color: "text.secondary",
            flex: "none",
          }}>
          {t("Analytics.sideMenu.sentimental")}
        </Typography>
        <IconButton size="small" sx={{ color: "text.secondary" }} onClick={handleInfoClick("sentimental")}>
          <InfoOutlinedIcon sx={{ fontSize: "1.125rem" }} />
        </IconButton>
      </Box>
      {SentimentMenuItems.map((item, index) => (
        <Link
          key={index}
          href={item.url}
          sx={{
            color: "text.primary",
            fontWeight: "600",
            textDecoration: "none",
            "&:hover": { bgcolor: "background.paperMedium" },
            display: "block",
            py: 1.5,
            pl: 6,
            pr: 3,
          }}>
          {item.isPro && <ProTag sx={{ display: "inline", mr: 1, position: "relative", top: "-2px" }} />}
          {t(item.label as any)}
        </Link>
      ))}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        sx={{ pointerEvents: "auto" }}
        slotProps={{
          paper: {
            sx: {
              p: 2,
              maxWidth: 260,
              fontSize: "0.95rem",
              borderRadius: 2,
              boxShadow: 3,
            },
          },
        }}>
        <Typography variant="body2" sx={{ color: "text.primary" }}>
          {popoverType === "technical" && t("Analytics.sideMenu.technicalInfo")}
          {popoverType === "fundamental" && t("Analytics.sideMenu.fundamentalInfo")}
          {popoverType === "sentimental" && t("Analytics.sideMenu.sentimentalInfo")}
        </Typography>
      </Popover>
    </Box>
  );
}
